import { BufferGeometry, Float32BufferAttribute, Group, LineBasicMaterial, LineSegments, Color, Box3 } from 'three';
import { ViewerEvents } from './Viewer';
import { areNotEqual } from './utils/geomUtils';
import { isPart3D } from './Objects/Part3D';
const primaryGridColor = new Color(0x555555);
const secondaryGridColor = new Color(0xa1a1a1);
const whiteColor = new Color(Color.NAMES.white);
const defaultGridSize = 20;
export class Grid extends Group {
    constructor(viewer, primaryGridSize, subdivisionsCount, primaryCellsCount = defaultGridSize) {
        super();
        this.viewer = viewer;
        this.primaryGridSizeInitial = primaryGridSize;
        this.subdivisionsCountInitial = subdivisionsCount;
        this.primaryCellsCountInitial = primaryCellsCount;
        this.buildGrid(primaryGridSize, subdivisionsCount, primaryCellsCount);
        this.onObjectsAdded = this.onObjectsAdded.bind(this);
        this.onObjectTransformed = this.onObjectTransformed.bind(this);
        this.onViewerCleared = this.onViewerCleared.bind(this);
        this.viewer.subscribe(ViewerEvents.AddObjects, this.onObjectsAdded);
        this.viewer.subscribe(ViewerEvents.ObjectTransformChanged, this.onObjectTransformed);
        this.viewer.subscribe(ViewerEvents.Cleared, this.onViewerCleared);
    }
    reset() {
        this.buildGrid(this.primaryGridSizeInitial, this.subdivisionsCountInitial, this.primaryCellsCountInitial);
        this.position.setZ(0);
    }
    dispose() {
        this.viewer.unsubscribe(ViewerEvents.AddObjects, this.onObjectsAdded);
        this.viewer.unsubscribe(ViewerEvents.ObjectTransformChanged, this.onObjectTransformed);
        this.viewer.unsubscribe(ViewerEvents.Cleared, this.onViewerCleared);
    }
    buildGrid(primaryGridSize, secondaryGridCellsPerPrimary, primaryCellsCount) {
        this.primaryGridSize = primaryGridSize;
        this.subdivisionsCount = secondaryGridCellsPerPrimary;
        this.primaryCellsCount = primaryCellsCount;
        this.clear();
        const geometry = new BufferGeometry();
        geometry.setAttribute('position', this.computePositionAttribute(primaryGridSize, secondaryGridCellsPerPrimary, primaryCellsCount));
        geometry.setAttribute('color', this.computeColorAttribute(primaryCellsCount, secondaryGridCellsPerPrimary));
        const material = new LineBasicMaterial({
            vertexColors: true,
        });
        this.add(new LineSegments(geometry, material));
    }
    computePositionAttribute(primaryGridSize, secondaryGridCellsPerPrimary, primaryCellsCount) {
        // 20 x 20 grid
        const step = primaryGridSize / secondaryGridCellsPerPrimary;
        const size = primaryCellsCount * primaryGridSize;
        const halfSize = size / 2;
        const vertices = [];
        const linesCount = secondaryGridCellsPerPrimary * primaryCellsCount;
        for (let i = 0, k = -halfSize; i <= linesCount; i += 1, k += step) {
            vertices.push(-halfSize, k, 0, halfSize, k, 0);
            vertices.push(k, -halfSize, 0, k, halfSize, 0);
        }
        return new Float32BufferAttribute(vertices, 3);
    }
    computeColorAttribute(primaryCellsCount, subdivisionsCount) {
        const colors = [];
        const linesCount = subdivisionsCount * primaryCellsCount;
        for (let i = 0, j = 0; i <= linesCount; i++) {
            let color;
            if (i % subdivisionsCount === 0) {
                color = primaryGridColor;
            }
            else if (i % (linesCount / 2) === 0) {
                color = whiteColor;
            }
            else {
                color = secondaryGridColor;
            }
            // since we created 4 points to define two lines we need to add 4 color attributes
            for (let k = 0; k < 4; k++) {
                color.toArray(colors, j);
                j += 3;
            }
        }
        return new Float32BufferAttribute(colors, 3);
    }
    onObjectsAdded({ payload: uuids }) {
        uuids.forEach((uuid) => {
            const object = this.viewer.getObjectByUUID(uuid);
            if (object) {
                const box = new Box3().setFromObject(object);
                if (!box.isEmpty() && box.max.z > this.position.z) {
                    this.position.setZ(box.max.z);
                }
                const gridSize = this.primaryCellsCount * this.primaryGridSize / 2;
                const xMin = Math.abs(Math.min(-gridSize, box.min.x));
                const xMax = Math.max(gridSize, box.max.x);
                const yMin = Math.abs(Math.min(-gridSize, box.min.y));
                const yMax = Math.max(gridSize, box.max.y);
                const maxDelta = Math.max(xMin, xMax, yMin, yMax);
                if (maxDelta > gridSize) {
                    const newCellsCount = Math.ceil(maxDelta / this.primaryGridSize * 1.1) * 2;
                    this.buildGrid(this.primaryGridSize, this.subdivisionsCount, newCellsCount);
                }
            }
        });
    }
    onObjectTransformed() {
        const sceneBox = this.viewer.getSceneBoundingBox([isPart3D]);
        if (areNotEqual(this.position.z, sceneBox.max.z)) {
            this.position.setZ(Math.max(sceneBox.max.z, 0));
        }
        const gridSize = this.primaryCellsCount * this.primaryGridSize / 2;
        const xMin = Math.abs(sceneBox.min.x);
        const xMax = sceneBox.max.x;
        const yMin = Math.abs(sceneBox.min.y);
        const yMax = sceneBox.max.y;
        const maxDelta = Math.max(xMin, xMax, yMin, yMax);
        if (maxDelta !== gridSize) {
            const newCellsCount = Math.ceil(maxDelta / this.primaryGridSize * 1.1) * 2;
            this.buildGrid(this.primaryGridSize, this.subdivisionsCount, newCellsCount);
        }
    }
    onViewerCleared() {
        this.reset();
    }
}
